import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle, Circle, AlertCircle, FileText, Lightbulb } from 'lucide-react'
import { useArticlesStore } from '@/lib/stores/articlesStore'
import { Button, Card, CardContent, CardHeader, CardTitle, Badge, Input, Spinner, UrlAutocomplete } from '@/components/ui'
import type { Proposal, PendingInput } from '@/types'

export function ProposalDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { fetchProposal, updateProposal } = useArticlesStore()
  const [proposal, setProposal] = useState<Proposal | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [userInputs, setUserInputs] = useState<Record<string, string>>({})

  useEffect(() => {
    const load = async () => {
      if (!id) return
      const data = await fetchProposal(id)
      setProposal(data)
      if (data?.user_inputs) {
        setUserInputs(data.user_inputs)
      }
      setIsLoading(false)
    }
    load()
  }, [id, fetchProposal])

  const handleInputChange = (inputId: string, value: string) => {
    setUserInputs((prev) => ({ ...prev, [inputId]: value }))
  }

  const handleSaveInputs = async () => {
    if (!proposal) return
    await updateProposal(proposal.id, { user_inputs: userInputs })
    const data = await fetchProposal(proposal.id)
    setProposal(data)
  }

  const allRequiredAnswered = proposal?.pending_inputs?.every(
    (input) => !input.required || userInputs[input.id]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    )
  }

  if (!proposal) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Proposta non trovata</p>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/proposals"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna alle proposte
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Proposta #{proposal.id.slice(0, 8)}
            </h1>
            <p className="text-gray-500 mt-1">
              Articolo: {(proposal as unknown as Record<string, unknown>).article_title as string || 'N/A'}
            </p>
            <Badge
              variant={
                proposal.status === 'pending_inputs'
                  ? 'warning'
                  : proposal.status === 'ready'
                  ? 'success'
                  : proposal.status === 'applied'
                  ? 'info'
                  : 'danger'
              }
              className="mt-2"
            >
              {proposal.status === 'pending_inputs'
                ? 'In attesa risposte'
                : proposal.status === 'ready'
                ? 'Pronta per review'
                : proposal.status === 'applied'
                ? 'Applicata'
                : 'Scartata'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Workflow:</strong> Queste proposte vengono salvate nel database.
          Settimanalmente, chiedi a Claude Code di revisionarle e implementare le modifiche strutturali con il plugin Frontend Design.
        </p>
      </div>

      {/* Structural Analysis */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary-600" />
            <CardTitle>Suggerimenti Strutturali</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {proposal.structural_analysis?.map((item) => (
              <div
                key={item.element}
                className="flex items-center justify-between py-2 border-b last:border-0"
              >
                <div className="flex items-center gap-3">
                  {item.present ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-300" />
                  )}
                  <span className="font-medium">{item.element}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      item.action === 'keep'
                        ? 'success'
                        : item.action === 'add'
                        ? 'warning'
                        : 'info'
                    }
                  >
                    {item.action === 'keep'
                      ? 'OK'
                      : item.action === 'add'
                      ? 'Da aggiungere'
                      : 'Da migliorare'}
                  </Badge>
                  {item.priority === 'high' && (
                    <Badge variant="danger">Priorità</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Content Improvements */}
      {proposal.content_improvements && proposal.content_improvements.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              <CardTitle>Suggerimenti Contenuto</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {proposal.content_improvements.map((improvement, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="info">{improvement.type}</Badge>
                    <span className="text-sm text-gray-500">{improvement.section}</span>
                  </div>
                  <p className="text-sm">{improvement.suggested}</p>
                  <p className="text-xs text-gray-500 mt-1">{improvement.reason}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Copy Improvements */}
      {proposal.copy_improvements && proposal.copy_improvements.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Suggerimenti Copy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {proposal.copy_improvements.map((copy, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium mb-1">{copy.element}</p>
                  {copy.current && (
                    <p className="text-sm text-red-600 line-through mb-1">{copy.current}</p>
                  )}
                  <p className="text-sm text-green-600">{copy.proposed}</p>
                  <p className="text-xs text-gray-500 mt-1">{copy.reasoning}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Inputs (if any) */}
      {proposal.pending_inputs && proposal.pending_inputs.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              <CardTitle>Risposte richieste</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {proposal.pending_inputs.map((input: PendingInput) => (
                <div key={input.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {input.question}
                    {input.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {input.type === 'url' && (
                    <UrlAutocomplete
                      value={userInputs[input.id] || ''}
                      onChange={(value) => handleInputChange(input.id, value)}
                      placeholder={input.placeholder || 'https://...'}
                    />
                  )}
                  {input.type === 'text' && (
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={userInputs[input.id] || ''}
                      onChange={(e) => handleInputChange(input.id, e.target.value)}
                      placeholder={input.placeholder}
                      rows={3}
                    />
                  )}
                  {!['url', 'text'].includes(input.type) && (
                    <Input
                      type="text"
                      value={userInputs[input.id] || ''}
                      onChange={(e) => handleInputChange(input.id, e.target.value)}
                      placeholder={input.placeholder || 'Inserisci risposta...'}
                    />
                  )}
                  {input.suggested && (
                    <p className="mt-1 text-sm text-gray-500">
                      Suggerimento: {input.suggested}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <Button onClick={handleSaveInputs} disabled={!allRequiredAnswered}>
                Salva risposte
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SERP Data */}
      {proposal.serp_data && (
        <Card>
          <CardHeader>
            <CardTitle>Dati SERP</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-2">
              Keyword: <strong>{proposal.serp_data.keyword}</strong>
            </p>
            {proposal.serp_data.people_also_ask && proposal.serp_data.people_also_ask.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">People Also Ask:</p>
                <ul className="list-disc list-inside text-sm text-gray-600">
                  {proposal.serp_data.people_also_ask.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
